import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'
import type { ParlaysResponse, PicksResponse } from '@/types/miniApp'

async function fetchPicks(date?: string, runType?: string): Promise<PicksResponse> {
  return (
    await apiClient.get('/api/mini-app/picks', {
      params: { ...(date ? { target_date: date } : {}), ...(runType ? { run_type: runType } : {}) },
    })
  ).data
}

async function fetchParlays(date?: string, runType?: string): Promise<ParlaysResponse> {
  return (
    await apiClient.get('/api/mini-app/parlays', {
      params: { ...(date ? { target_date: date } : {}), ...(runType ? { run_type: runType } : {}) },
    })
  ).data
}

async function fetchPortfolioDates(year: number, month: number): Promise<{ dates: string[] }> {
  return (await apiClient.get('/api/mini-app/portfolio-dates', { params: { year, month } })).data
}

// Sprint "Cierre quirúrgico UI/Histórico" (25/09/2026): días con publicación oficial real de
// un mes, para pintar los puntos del calendario de `DateNav` -- nunca recalcula nada, solo
// indica qué fechas tienen `DailyPortfolio.is_current=True`.
export function usePortfolioDates(year: number, month: number, enabled: boolean) {
  return useQuery<{ dates: string[] }>({
    queryKey: ['mini-app', 'portfolio-dates', year, month],
    queryFn: () => fetchPortfolioDates(year, month),
    enabled,
    staleTime: 5 * 60_000,
  })
}

// P0.31 (26/08/2026, rediseño Mini App -- Sección 3/9 del encargo): la navegación activa lee
// EXCLUSIVAMENTE la cartera vigente (`run_type='daily'` desde el corte P0.30) -- se retira
// por completo el merge cliente RUN1+RUN2 de Fase R.11 (`mergePickLists`), que mantenía viva
// la experiencia "2 franjas" incluso después del corte oficial. `runType` explícito
// ('run1'/'run2') sigue soportado tal cual, sin cambios -- es el camino de Histórico
// (Sección 8 del encargo), nunca se borra el acceso a esas carteras legado.
export function usePicks(enabled: boolean, date?: string, runType?: string) {
  return useQuery<PicksResponse>({
    queryKey: ['mini-app', 'picks', date ?? 'today', runType ?? 'current'],
    queryFn: () => fetchPicks(date, runType),
    enabled,
    staleTime: 30_000,
  })
}

// P0.31 (Sección 3/9 del encargo): mismo criterio que `usePicks` -- sin `runType`, pide
// directamente la cartera vigente (DAILY), nunca reconstruye RUN1+RUN2 en el cliente.
export function useParlays(enabled: boolean, date?: string, runType?: string) {
  return useQuery<ParlaysResponse>({
    queryKey: ['mini-app', 'parlays', date ?? 'today', runType ?? 'current'],
    queryFn: () => fetchParlays(date, runType),
    enabled,
    staleTime: 30_000,
  })
}

/** P0.26 (Sección 2/24 del encargo) -- explicación comercial BAJO DEMANDA, solo cuando el
 * cliente abre el detalle de un pick concreto (`PickDetailSheet`). Deliberadamente NO viene
 * en la respuesta masiva de `/picks`/`/parlays` -- el motor de explicación hace varias
 * consultas reales por decisión y precomputarlo ahí regresionaba el batch-loading real
 * (`test_r15_portfolio_batch_loading.py`, ver `_build_leg_detail`). `enabled` solo cuando
 * el drawer está realmente abierto para un `decisionRecordId` real.
 *
 * Sprint "Corrección Puntual -- Qué puede fallar + A vigilar" (10/09/2026): además de
 * `human_explanation` (A, "Por qué le gusta a JA"), ahora también `risk_explanation` (B,
 * "Qué puede fallar") y `watch_explanation` (C, "A vigilar") -- datos reales construidos
 * por el backend (`HumanExplanationEngine.explain_risks()`/`explain_watch()`), nunca
 * inventados en el frontend. */
export function usePickExplanation(decisionRecordId: number | null | undefined) {
  return useQuery<{ human_explanation: string[]; risk_explanation: string[]; watch_explanation: string[] }>({
    queryKey: ['mini-app', 'pick-explanation', decisionRecordId],
    queryFn: async () =>
      (await apiClient.get(`/api/mini-app/picks/${decisionRecordId}/explanation`)).data,
    enabled: decisionRecordId != null,
    staleTime: 5 * 60_000,
  })
}
